import { State } from '@hookstate/core';
import delay from 'delay';
import { createState } from '../../lib/create-state';
import type { IntervalHandle } from '../../lib/create-timer';
import { isSuspended, onSuspend } from '../../lib/suspension';

export type MessageModal = {
    name: string;
    isOpen: boolean;
    closeBehavior?: 'close' | 'reload';
    hideCloseButton?: boolean;
    showButton?: boolean;
    buttonText?: string;
    buttonBehavior?: 'reload';
};

export type MetaState = {
    currency: string;
    autoDaub: boolean;
    sound: boolean;
    dauberColor: string;
    dauberStyle: string;

    timer: {
        id?: IntervalHandle;
        date: number;
    };

    enabled: {
        buying: boolean;
    };

    loaders: {
        screenConfig: boolean;
        buying: boolean;
        accountStatus: boolean;
        recWinners: boolean;
        pickedCards: boolean;
    };

    loaded: {
        screenConfig: boolean;
        recWinners: boolean;
    };

    modals: {
        buy: boolean;
        autoBuy: boolean;
        gameCancelled: boolean;
        winner: boolean;
        settings: boolean;
        helpDesk: boolean;
        recWinners: boolean;
        resGaming: boolean;
        freeCards: boolean;
        partWinners: boolean;
        gameInfo: boolean;
        gameInfoFireBallEnabled: boolean;
        jackpot: boolean;
        pickCards: boolean;
        marketing: boolean;
        dauberStyle: boolean;
        dauberColor: boolean;
        fpeToken: boolean;
        errorBuy: MessageModal;
        msgCommon: MessageModal;
    };

    panels: {
        partWinner: boolean;
    };

    footers: {
        sideGames: boolean;
    };
};

export const DEFAULT_META_STATE: MetaState = {
    currency: '',
    autoDaub: (localStorage.getItem('autoDaub') || 'true') === 'true',
    sound: (localStorage.getItem('sound') || 'true') === 'true',
    dauberColor: localStorage.getItem('dauberColor') || '',
    dauberStyle: localStorage.getItem('dauberStyle') || '',

    timer: {
        date: new Date().getTime(),
    },

    enabled: {
        buying: false,
    },

    loaders: {
        screenConfig: false,
        buying: false,
        accountStatus: false,
        recWinners: false,
        pickedCards: false,
    },

    loaded: {
        screenConfig: false,
        recWinners: false,
    },

    modals: {
        buy: false,
        autoBuy: false,
        gameCancelled: false,
        winner: false,
        settings: false,
        helpDesk: false,
        recWinners: false,
        resGaming: false,
        freeCards: false,
        partWinners: false,
        gameInfo: false,
        gameInfoFireBallEnabled: true,
        jackpot: false,
        pickCards: false,
        marketing: false,
        dauberStyle: false,
        dauberColor: false,
        fpeToken: false,
        errorBuy: {
            name: '',
            isOpen: false,
        },
        msgCommon: {
            name: '',
            isOpen: false,
        },
    },

    panels: {
        partWinner: false,
    },

    footers: {
        sideGames: true,
    },
};

export type MetaStateModals = MetaState['modals'];

export const [meta, resetMeta] = createState<MetaState>(DEFAULT_META_STATE);

const DEFAULT_MESSAGE_MODAL_BEHAVIOR: Omit<MessageModal, 'name' | 'isOpen'> = {
    closeBehavior: 'close',
    hideCloseButton: false,
    showButton: false,
    buttonText: undefined,
    buttonBehavior: undefined,
};

export type PartialModalValue<T> = T extends MessageModal
    ? Partial<MessageModal>
    : T;

export type ModalOptions = {
    autoCloseDelay?: number;
    autoClose?: boolean;
    abortController?: AbortController;
};

export async function setModalVisibility<
    Key extends keyof MetaStateModals,
    Value extends PartialModalValue<MetaStateModals[Key]>
>(key: Key, value: Value, options?: ModalOptions) {
    if (isSuspended()) return;
    const $options = {
        autoCloseDelay: 15000,
        ...options,
    };

    let booleanState: State<boolean>;

    if (typeof value === 'object') {
        const state = meta.modals[key] as State<MessageModal>;
        booleanState = state.isOpen;
        const isOpeningMessageModal =
            'name' in value || (value as Partial<MessageModal>).isOpen === true;

        state.merge({
            ...(isOpeningMessageModal
                ? DEFAULT_MESSAGE_MODAL_BEHAVIOR
                : state.value),
            ...(value as MessageModal),
        });
    } else {
        const state = (booleanState = meta.modals[key] as State<boolean>);
        state.set(value);
    }

    if (typeof $options?.autoClose !== 'undefined' && booleanState.value) {
        const controller = options?.abortController || new AbortController();
        const unsubscribe = onSuspend(() => controller.abort());
        try {
            await delay($options.autoCloseDelay, {
                signal: controller.signal,
            });

            if (!isSuspended()) booleanState.set(false);
        } catch (error) {
        } finally {
            unsubscribe();
        }
    }
}

export const openModal = ({
    key,
    name,
    closeBehavior,
    hideCloseButton,
    showButton,
    buttonText,
    buttonBehavior,
    ...options
}: {
    key: keyof MetaStateModals;
    name?: string;
} & ModalOptions &
    Partial<Omit<MessageModal, 'name' | 'isOpen'>>) => {
    const messageModalBehavior = {
        closeBehavior,
        hideCloseButton,
        showButton,
        buttonText,
        buttonBehavior,
    };
    const value = name ? { name, isOpen: true, ...messageModalBehavior } : true;

    return setModalVisibility(key, value, options);
};

export const closeModal = (key: keyof MetaStateModals) => {
    if (isSuspended()) return;
    const state = meta.modals[key];

    if (typeof state.value === 'boolean') {
        (state as State<boolean>).set(false);
    } else {
        (state as State<MessageModal>).isOpen.set(false);
    }
};
